import type { ReactNode } from 'react';
import { Activity } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { Button, Skeleton, Badge } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useWorkspace } from '@/api/healthcheck';
import { WorkspaceActivation } from './WorkspaceActivation';

/**
 * Single GATE in front of the whole Healthcheck module. One `GET /workspace` call
 * decides what renders — so tenant-scoped child queries never fire (and never
 * flood 403s) until a workspace actually exists:
 *   - loading      → skeleton
 *   - error (real) → retry
 *   - result null  → WorkspaceActivation (onboarding, no children mounted)
 *   - workspace    → children (picker / console)
 */
export function WorkspaceGate({ children }: { children: ReactNode }) {
  const { data: workspace, isLoading, isError, refetch } = useWorkspace();

  if (isLoading) {
    return (
      <Container size="xl">
        <Header />
        <Section title="Loading">
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <Skeleton style={{ height: 28, width: 220 }} />
            <Skeleton style={{ height: 116 }} />
          </div>
        </Section>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container size="xl">
        <Header />
        <Section title="Healthcheck">
          <Alert
            tone="danger"
            title="Couldn't reach Healthcheck"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            The monitoring service is temporarily unavailable. Please try again.
          </Alert>
        </Section>
      </Container>
    );
  }

  // Org resolvable but no workspace yet → onboarding. Activating invalidates the
  // gate query, so this flips to the children branch on success automatically.
  if (workspace === null) {
    return (
      <Container size="xl">
        <Header />
        <WorkspaceActivation />
      </Container>
    );
  }

  return <>{children}</>;
}

function Header() {
  return (
    <PageHeader
      icon={<Activity size={22} />}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          Healthcheck &amp; Alerting
          <Badge tone="info" appearance="subtle" size="sm">
            Beta
          </Badge>
        </span>
      }
      description="Uptime monitors, latency tracking and incident alerting for your endpoints and services."
    />
  );
}
